/**
 * Admin Module Routes
 * Personnel, Buildings, Vehicles, Forms, Kitchen, Meters, Transfers, Security
 */
import React, { lazy, Suspense } from 'react';
import { Spin, Result } from 'antd';
import { Navigate } from 'react-router-dom';
import { RouteItem } from './index';
import {
  HomeOutlined, FileOutlined, PlusOutlined, TeamOutlined, FormOutlined,
  OrderedListOutlined, CalendarOutlined, CarryOutOutlined, AuditOutlined,
  BarChartOutlined, CalculatorOutlined, WalletOutlined, CarOutlined as VehicleOutlined,
  CoffeeOutlined, BookOutlined, PlayCircleOutlined, DashboardOutlined,
  SafetyOutlined, UserOutlined
} from '@ant-design/icons';
import { FaKitchenSet } from 'react-icons/fa6';
import { MdRestaurantMenu } from 'react-icons/md';

// Wrapper component to satisfy TS JSX typing with react-icons (same pattern as AdmBldEdit)
const RKitchen: React.FC<any> = (props) =>
  React.createElement(FaKitchenSet as unknown as React.ComponentType<any>, props) as React.ReactElement;
const RRestaurantMenu: React.FC<any> = (props) =>
  React.createElement(MdRestaurantMenu as unknown as React.ComponentType<any>, props) as React.ReactElement;

// Lazy load Admin components
// Forms
const AdmFormList = lazy(() => import('../pages/admin/forms/AdmFormList'));
const AdmFormsCreate = lazy(() => import('../pages/admin/forms/AdmFormsCreate'));
const AdmFormFill = lazy(() => import('../pages/admin/forms/AdmFormFill'));
const AdmFormEdit = lazy(() => import('../pages/admin/forms/AdmFormEdit'));
const AdmFormView = lazy(() => import('../pages/admin/forms/AdmFormView'));
const AdmFormGroupCreate = lazy(() => import('../pages/admin/forms/AdmFormGroupCreate'));
const FormDefinitionsList = lazy(() => import('../components/common/Forms/FormDefinitionsList'));
const FormDefinitionEditor = lazy(() => import('../components/common/Forms/FormDefinitionEditor'));
const FormRecordDetailsList = lazy(() => import('../components/common/Forms/FormRecordDetailsList'));
const PdfToReactFormGenerator = lazy(() => import('../components/common/Forms/PdfToReactFormGenerator'));

// Personnel
const AdmPersList = lazy(() => import('../pages/admin/personnel/AdmPersList'));
const AdmPersCreate = lazy(() => import('../pages/admin/personnel/AdmPersCreate'));
const AdmPersEdit = lazy(() => import('../pages/admin/personnel/AdmPersEdit'));
const AdmPersDetail = lazy(() => import('../pages/admin/personnel/AdmPersDetail'));
const AdmPersFihrist = lazy(() => import('../pages/admin/personnel/AdmPersFihrist'));
const AdmPersImport = lazy(() => import('../pages/admin/personnel/AdmPersImport'));
const AdmPersDashboard = lazy(() => import('../pages/admin/personnel/AdmPersDashboard'));

// Buildings
const AdmBldList = lazy(() => import('../pages/admin/buildings/AdmBldList'));
const AdmBldCreate = lazy(() => import('../pages/admin/buildings/AdmBldCreate'));
const AdmBldEdit = lazy(() => import('../pages/admin/buildings/AdmBldEdit'));
const AdmBldDetail = lazy(() => import('../pages/admin/buildings/AdmBldDetail'));
const AdmBldReservList = lazy(() => import('../pages/admin/buildings/AdmBldReservList'));
const AdmBldReservNew = lazy(() => import('../pages/admin/buildings/AdmBldReservNew'));
const AdmBldReservEdit = lazy(() => import('../pages/admin/buildings/AdmBldReservEdit'));
const AdmBldReservImport = lazy(() => import('../pages/admin/buildings/AdmBldReservImport'));
const AdmBldOccupancy = lazy(() => import('../pages/admin/buildings/AdmBldOccupancy'));
const AdmBldDashboard = lazy(() => import('../pages/admin/buildings/AdmBldDashboard'));

// Vehicles
const AdmVehicleList = lazy(() => import('../pages/admin/vehicles/AdmVehicleList'));
const AdmVehicleDetail = lazy(() => import('../pages/admin/vehicles/AdmVehicleDetail'));

// Kitchen
const AdmKitchRecipeList = lazy(() => import('../pages/admin/kitchen/AdmKitchRecipeList'));
const AdmKitchRecipeCreate = lazy(() => import('../pages/admin/kitchen/AdmKitchRecipeCreate'));
const AdmKitchRecipeEdit = lazy(() => import('../pages/admin/kitchen/AdmKitchRecipeEdit'));
const AdmKitchRecipeDetail = lazy(() => import('../pages/admin/kitchen/AdmKitchRecipeDetail'));
const AdmKitchMenuList = lazy(() => import('../pages/admin/kitchen/AdmKitchMenuList'));
const AdmKitchMenuCreate = lazy(() => import('../pages/admin/kitchen/AdmKitchMenuCreate'));
const AdmKitchMenuEdit = lazy(() => import('../pages/admin/kitchen/AdmKitchMenuEdit'));
const AdmKitchMenuCalc = lazy(() => import('../pages/admin/kitchen/AdmKitchMenuCalc'));
const AdmKitchMenuWeekly = lazy(() => import('../pages/admin/kitchen/AdmKitchMenuWeekly'));

// Meters
const AdmMetersList = lazy(() => import('../pages/admin/meters/AdmMetersList'));
const AdmMetersCreate = lazy(() => import('../pages/admin/meters/AdmMetersCreate'));
const AdmMetersEdit = lazy(() => import('../pages/admin/meters/AdmMetersEdit'));
const AdmMeterDetail = lazy(() => import('../pages/admin/meters/AdmMeterDetail'));
const AdmMeterBulkReading = lazy(() => import('../pages/admin/meters/AdmMeterBulkReading'));

// Transfers
const TravelRequestList = lazy(() => import('../pages/admin/transfers/TravelRequestList'));
const NewTravelRequestForm = lazy(() => import('../pages/admin/transfers/NewTravelRequestForm'));
const PendingApprovals = lazy(() => import('../pages/admin/transfers/PendingApprovals'));
const TravelAdminDashboard = lazy(() => import('../pages/admin/transfers/TravelAdminDashboard'));
const EntitlementSetupAdmin = lazy(() => import('../pages/admin/transfers/EntitlementSetupAdmin'));
const PersonnelEntitlements = lazy(() => import('../components/user/PersonnelEntitlements'));

// UnderConstruction component
const UnderConstruction = ({ title }: { title: string }) => (
  <Result status="info" title={`${title} Module`} subTitle="Under Construction." />
);

// Suspense wrapper
const withSuspense = (Component: React.LazyExoticComponent<any>) => (
  <Suspense fallback={<Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: '20%' }} />}>
    <Component />
  </Suspense>
);

// Special wrapper for NewTravelRequestForm that requires onSuccess prop
const TravelRequestFormWrapper = () => <NewTravelRequestForm onSuccess={() => {}} />;
const NewTravelRequestFormLazy = () => (
  <Suspense fallback={<Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: '20%' }} />}>
    <TravelRequestFormWrapper />
  </Suspense>
);

export const adminRoutes: Record<string, RouteItem> = {
  admin: {
    path: '/admin',
    element: null,
    label: 'Admin',
    showInMenu: false,
    children: {
      dashboard: {
        path: '/admin/dashboard',
        element: <UnderConstruction title="Admin" />,
        label: 'Dashboard',
        icon: <HomeOutlined />,
        showInMenu: true
      },
      forms: {
        path: '/admin/forms',
        element: withSuspense(AdmFormList),
        label: 'Forms',
        icon: <FormOutlined />,
        showInMenu: true,
        grandchildren: {
          list: {
            path: '/admin/forms/list',
            element: withSuspense(AdmFormList),
            label: 'Form List',
            icon: <FileOutlined />,
            showInMenu: true
          },
          definitions: {
            path: '/admin/forms/definitions',
            element: withSuspense(FormDefinitionsList),
            label: 'Form Definitions',
            icon: <OrderedListOutlined />,
            showInMenu: true
          },
          definitions_create: {
            path: '/admin/forms/definitions/create',
            element: withSuspense(PdfToReactFormGenerator),
            label: 'Create Form Definition',
            showInMenu: false
          },
          definitions_edit: {
            path: '/admin/forms/definitions/edit/:id',
            element: withSuspense(FormDefinitionEditor),
            label: 'Edit Form Definition',
            showInMenu: false
          },
          list_record_details: {
            path: '/admin/forms/list_record_details',
            element: withSuspense(FormRecordDetailsList),
            label: 'Form Record Details',
            icon: <FileOutlined />,
            showInMenu: true
          },
          group: {
            path: '/admin/forms/gr_create',
            element: withSuspense(AdmFormGroupCreate),
            label: 'New Form Group',
            icon: <PlusOutlined />,
            showInMenu: true
          },
          create: {
            path: '/admin/forms/create',
            element: withSuspense(AdmFormsCreate),
            label: 'Create Form',
            icon: <PlusOutlined />,
            showInMenu: true
          },
          fill: {
            path: '/admin/forms/fill',
            element: withSuspense(AdmFormFill),
            label: 'Fill Form',
            showInMenu: true
          },
          edit: {
            path: '/admin/forms/edit/:id',
            element: withSuspense(AdmFormEdit),
            label: 'Edit Form',
            showInMenu: false
          },
          view: {
            path: '/admin/forms/view/:id',
            element: withSuspense(AdmFormView),
            label: 'View Form',
            showInMenu: false
          }
        }
      },
      personnel: {
        path: '/admin/personnel',
        element: withSuspense(AdmPersList),
        label: 'Personnel',
        icon: <TeamOutlined />,
        showInMenu: false,
        children: {
          dashboard: {
            path: '/admin/personnel/dashboard',
            element: withSuspense(AdmPersDashboard),
            label: 'Dashboard',
            icon: <DashboardOutlined />,
            showInMenu: true
          },
          create: {
            path: '/admin/personnel/create',
            element: withSuspense(AdmPersCreate),
            label: 'New Personnel',
            icon: <PlusOutlined />,
            showInMenu: true
          },
          list: {
            path: '/admin/personnel/list',
            element: withSuspense(AdmPersList),
            label: 'List Personnel',
            icon: <FileOutlined />,
            showInMenu: true
          },
          fihrist: {
            path: '/admin/personnel/fihrist',
            element: withSuspense(AdmPersFihrist),
            label: 'Fihrist',
            icon: <UserOutlined />,
            showInMenu: true
          },
          import: {
            path: '/admin/personnel/import',
            element: withSuspense(AdmPersImport),
            label: 'Excel Import',
            icon: <FileOutlined />,
            showInMenu: true
          },
          ById: {
            path: '/admin/personnel/show/:id',
            element: withSuspense(AdmPersDetail),
            label: 'Personnel Detail',
            showInMenu: false
          },
          byId: {
            path: '/admin/personnel/edit/:id',
            element: withSuspense(AdmPersEdit),
            label: 'Edit Personnel',
            showInMenu: false
          }
        }
      },
      transfers: {
        path: '/admin/transfers',
        element: withSuspense(TravelRequestList),
        label: 'Transfers',
        icon: <CarryOutOutlined />,
        showInMenu: true,
        children: {
          requestList: {
            path: '/admin/transfers/list',
            element: withSuspense(TravelRequestList),
            label: 'Taleplerim',
            icon: <FileOutlined />,
            showInMenu: true
          },
          newRequest: {
            path: '/admin/transfers/new',
            element: <NewTravelRequestFormLazy />,
            label: 'Yeni Seyahat Talebi',
            icon: <PlusOutlined />,
            showInMenu: true
          },
          pendingApprovals: {
            path: '/admin/transfers/pending',
            element: withSuspense(PendingApprovals),
            label: 'Onay Bekleyenler',
            icon: <AuditOutlined />,
            showInMenu: true
          },
          adminDashboard: {
            path: '/admin/transfers/dashboard',
            element: withSuspense(TravelAdminDashboard),
            label: 'Tüm Talepler (Admin)',
            icon: <BarChartOutlined />,
            showInMenu: true
          },
          entitlementSetup: {
            path: '/admin/transfers/entitlement-setup',
            element: withSuspense(EntitlementSetupAdmin),
            label: 'Hak Ediş Girişi',
            icon: <CalculatorOutlined />,
            showInMenu: true
          },
          kalanHaklarim: {
            path: '/admin/transfers/entitlements',
            element: withSuspense(PersonnelEntitlements),
            label: 'Kalan Haklarım',
            icon: <WalletOutlined />,
            showInMenu: true
          }
        }
      },
      buildings: {
        path: '/admin/buildings',
        element: <Navigate to="/admin/buildings/dashboard" replace />,
        label: 'Buildings',
        icon: <HomeOutlined />,
        showInMenu: false,
        grandchildren: {
          dashboard: {
            path: '/admin/buildings/dashboard',
            element: withSuspense(AdmBldDashboard),
            label: 'Dashboard',
            icon: <HomeOutlined />,
            showInMenu: true
          },
          create: {
            path: '/admin/buildings/create',
            element: withSuspense(AdmBldCreate),
            label: 'New Building',
            icon: <PlusOutlined />,
            showInMenu: true
          },
          list: {
            path: '/admin/buildings/list',
            element: withSuspense(AdmBldList),
            label: 'List Buildings',
            icon: <FileOutlined />,
            showInMenu: true
          },
          book: {
            path: '/admin/buildings/reservation',
            element: withSuspense(AdmBldReservList),
            label: 'Reservation',
            icon: <CalendarOutlined />,
            showInMenu: true
          },
          ById: {
            path: '/admin/buildings/show/:id',
            element: withSuspense(AdmBldDetail),
            label: 'Building Detail',
            showInMenu: false
          },
          byId: {
            path: '/admin/buildings/edit/:id',
            element: withSuspense(AdmBldEdit),
            label: 'Edit Building',
            showInMenu: false
          },
          newrez: {
            path: '/admin/buildings/reservation/new',
            element: withSuspense(AdmBldReservNew),
            label: 'New Booking',
            showInMenu: false
          },
          importrez: {
            path: '/admin/buildings/reservation/import',
            element: withSuspense(AdmBldReservImport),
            label: 'Import Bookings',
            showInMenu: false
          },
          byid: {
            path: '/admin/buildings/reservation/edit/:id',
            element: withSuspense(AdmBldReservEdit),
            label: 'Edit Booking',
            showInMenu: false
          },
          occup: {
            path: '/admin/buildings/Occupancy',
            element: withSuspense(AdmBldOccupancy),
            label: 'Occupancy',
            icon: <TeamOutlined />,
            showInMenu: true
          }
        }
      },
      vehicles: {
        path: '/admin/vehicles',
        element: withSuspense(AdmVehicleList),
        label: 'Vehicles',
        icon: <VehicleOutlined />,
        showInMenu: true,
        children: {
          detail: {
            path: '/admin/vehicles/show/:id',
            element: withSuspense(AdmVehicleDetail),
            label: 'Vehicle Detail',
            showInMenu: false
          }
        }
      },
      kitchen: {
        path: '/admin/kitchen',
        element: <UnderConstruction title="Kitchen" />,
        label: 'Kitchen',
        icon: <RKitchen />,
        showInMenu: false,
        children: {
          recipes: {
            path: '/admin/kitchen/recipes',
            element: withSuspense(AdmKitchRecipeList),
            label: 'Recipes',
            icon: <BookOutlined />,
            showInMenu: true
          },
          create: {
            path: '/admin/kitchen/recipes/create',
            element: withSuspense(AdmKitchRecipeCreate),
            label: 'Create Recipe',
            icon: <PlusOutlined />,
            showInMenu: false
          },
          edit: {
            path: '/admin/kitchen/recipes/edit/:id',
            element: withSuspense(AdmKitchRecipeEdit),
            label: 'Edit Recipe',
            showInMenu: false
          },
          show: {
            path: '/admin/kitchen/recipes/show/:id',
            element: withSuspense(AdmKitchRecipeDetail),
            label: 'Recipe Detail',
            showInMenu: false
          },
          menu: {
            path: '/admin/kitchen/menu',
            element: withSuspense(AdmKitchMenuList),
            label: 'Menu',
            icon: <RRestaurantMenu size={20} />,
            showInMenu: true
          },
          weekly: {
            path: '/admin/kitchen/menu/weekly',
            element: withSuspense(AdmKitchMenuWeekly),
            label: 'Weekly Planner',
            icon: <CalendarOutlined />,
            showInMenu: true
          },
          create2: {
            path: '/admin/kitchen/menu/create',
            element: withSuspense(AdmKitchMenuCreate),
            label: 'Create Menu',
            icon: <PlusOutlined />,
            showInMenu: false
          },
          edit2: {
            path: '/admin/kitchen/menu/edit/:id',
            element: withSuspense(AdmKitchMenuEdit),
            label: 'Edit Menu',
            showInMenu: false
          },
          calc: {
            path: '/admin/kitchen/menu/calc',
            element: withSuspense(AdmKitchMenuCalc),
            label: 'Menu Calc',
            icon: <CalculatorOutlined />,
            showInMenu: false
          }
        }
      },
      water: {
        path: '/admin/water',
        element: <UnderConstruction title="Water" />,
        label: 'Water',
        icon: <DashboardOutlined />,
        showInMenu: false
      },
      meters: {
        path: '/admin/meters',
        element: withSuspense(AdmMetersList),
        label: 'Meters',
        icon: <DashboardOutlined />,
        showInMenu: true,
        children: {
          list: {
            path: '/admin/meters/list',
            element: withSuspense(AdmMetersList),
            label: 'Meter List',
            icon: <FileOutlined />,
            showInMenu: true
          },
          create: {
            path: '/admin/meters/create',
            element: withSuspense(AdmMetersCreate),
            label: 'New Meter',
            icon: <PlusOutlined />,
            showInMenu: true
          },
          edit: {
            path: '/admin/meters/edit/:id',
            element: withSuspense(AdmMetersEdit),
            label: 'Edit Meter',
            showInMenu: false
          },
          detail: {
            path: '/admin/meters/detail/:id',
            element: withSuspense(AdmMeterDetail),
            label: 'Meter Detail',
            showInMenu: false
          },
          addreading: {
            path: '/admin/meters/add-reading',
            element: withSuspense(AdmMeterBulkReading),
            label: 'Add Reading',
            showInMenu: true
          }
        }
      },
      security: {
        path: '/admin/security',
        element: <UnderConstruction title="Security" />,
        label: 'Security',
        icon: <SafetyOutlined />,
        showInMenu: false
      }
    }
  }
};
